import React, { useState, useRef, useEffect } from "react";
import { DATA_PAGE_SEO } from "../../seo/pageSeoMeta";
import styled from "styled-components"
import { useTranslation } from 'react-i18next';
import { purpleColors, violetColors, slateColors, Section, Container, TitleHighlight, fontFamily } from './AppLandingPage/styles';
import { Helmet } from 'react-helmet-async';
import { 
  ArrowRight, 
  Clock, 
  BarChart3, 
  ArrowUpDown, 
  Plug, 
  Globe, 
  RefreshCw, 
  Search, 
  Brain, 
  Building2, 
  Activity, 
  Bus, 
  FileText 
} from 'lucide-react';
import { postApi } from "../../utils/api";
import { TextField, MultiLineTextField } from '../Controls/TextFields';
import { StyledTooltip } from '../TooltipStyles';

const HeroSection = styled(Section)`
  position: relative;
  overflow: hidden;
  background: linear-gradient(to bottom right, ${slateColors[50]}, white, ${purpleColors[50]});
  padding: 3rem 1.5rem;
  text-align: center;
  
  @media (min-width: 768px) {
    padding: 4rem 3rem;
  }
  
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: 
      linear-gradient(to right, ${purpleColors[500]}08 1px, transparent 1px),
      linear-gradient(to bottom, ${purpleColors[500]}08 1px, transparent 1px);
    background-size: 24px 24px;
    pointer-events: none;
  }
`;

const HeroContent = styled(Container)`
  position: relative;
  z-index: 10;
`;

const HeroTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: ${slateColors[900]};
  margin-bottom: 1rem;
  line-height: 1.1;
  
  @media (min-width: 768px) {
    font-size: 3.5rem;
  }
  
  @media (min-width: 1024px) {
    font-size: 4rem;
  }
`;

const HeroDescription = styled.p`
  font-size: 1.125rem;
  color: ${slateColors[600]};
  margin: 0 auto 2rem;
  max-width: 48rem;
  line-height: 1.6;
  
  @media (min-width: 768px) {
    font-size: 1.25rem;
  }
`;

const CTAButton = styled.button`
  padding: 0.875rem 1.75rem;
  background: linear-gradient(to right, ${purpleColors[600]}, ${violetColors[600]});
  color: white;
  border-radius: 9999px;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.2s;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  border: none;
  cursor: pointer;
  margin: 0 auto;
  
  &:hover {
    background: linear-gradient(to right, ${purpleColors[700]}, ${violetColors[600]});
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
    transform: translateY(-2px);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  svg {
    width: 1.125rem;
    height: 1.125rem;
  }
`;

const ContentSection = styled(Section)`
  background: white;
`;

const ContentContainer = styled(Container)``;

const SectionTitle = styled.h2`
  font-size: 2rem;
  font-weight: 700;
  color: ${slateColors[900]};
  margin-bottom: 1rem;
  text-align: center;
  
  @media (min-width: 768px) {
    font-size: 2.5rem;
    margin-bottom: 1.5rem;
  }
`;

const SectionDescription = styled.p`
  font-size: 1.125rem;
  color: ${slateColors[600]};
  max-width: 48rem;
  margin: 0 auto 3rem;
  text-align: center;
  line-height: 1.6;
  
  @media (min-width: 768px) {
    font-size: 1.25rem;
    margin-bottom: 4rem;
  }
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  max-width: 48rem;
  margin: 0 auto;
  
  p {
    font-size: 1.125rem;
    color: ${slateColors[600]};
    line-height: 1.7;
    margin: 0;
    text-align: center;
    
    @media (min-width: 768px) {
      font-size: 1.25rem;
    }
  }
`;

const CountriesSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  margin-top: 2rem;
  
  @media (min-width: 768px) {
    margin-top: 3rem;
  }
`;

const CountriesTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${slateColors[900]};
  margin: 0;
  
  @media (min-width: 768px) {
    font-size: 1.5rem;
  }
`;

const CountriesList = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.75rem;
  
  @media (min-width: 768px) {
    gap: 1rem;
  }
`;

const CountryBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: ${slateColors[50]};
  border: 1px solid ${slateColors[200]};
  border-radius: 0.5rem;
  font-size: 1rem;
  font-weight: 600;
  color: ${slateColors[900]};
  
  @media (min-width: 768px) {
    padding: 0.625rem 1.25rem;
    font-size: 1.125rem;
  }
`;

const Flag = styled.span`
  font-size: 1.25rem;
  line-height: 1;
  
  @media (min-width: 768px) {
    font-size: 1.5rem;
  }
`;

const OperatorsSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  margin-top: 3rem;
  
  @media (min-width: 768px) {
    margin-top: 4rem;
  }
`;

const OperatorsTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${slateColors[900]};
  margin: 0;
  
  @media (min-width: 768px) {
    font-size: 1.5rem;
  }
`;

const OperatorsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 1rem;
  
  @media (min-width: 768px) {
    gap: 1.5rem;
  }
`;

const OperatorLogo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem;
  background: white;
  border: 1px solid ${slateColors[200]};
  border-radius: 0.75rem;
  transition: all 0.3s;
  width: 90px;
  height: 90px;
  
  @media (min-width: 640px) {
    width: 100px;
    height: 100px;
  }
  
  @media (min-width: 768px) {
    width: 110px;
    height: 110px;
  }
  
  &:hover {
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    border-color: ${slateColors[300]};
    transform: translateY(-2px);
  }
  
  img {
    max-width: 100%;
    max-height: 100%;
    width: auto;
    height: auto;
    object-fit: contain;
  }
`;

const FeaturesSection = styled(Section)`
  background: ${slateColors[50]};
`;

const FeaturesContainer = styled(Container)``;

const FeaturesHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
  
  @media (min-width: 768px) {
    margin-bottom: 4rem;
  }
`;

const FeaturesTitle = styled.h2`
  font-size: 2rem;
  font-weight: 700;
  color: ${slateColors[900]};
  margin-bottom: 1rem;
  
  @media (min-width: 768px) {
    font-size: 2.5rem;
  }
`;

const FeaturesDescription = styled.p`
  font-size: 1.125rem;
  color: ${slateColors[600]};
  max-width: 48rem;
  margin: 0 auto;
  line-height: 1.6;
  
  @media (min-width: 768px) {
    font-size: 1.25rem;
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 2.5rem;
  }
`;

const FeatureColumn = styled.div<{ variant?: 'features' | 'usecases' }>`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 2rem;
  border-radius: 1.5rem;
  background: ${props => props.variant === 'features' 
    ? `linear-gradient(to bottom right, ${purpleColors[50]}, white)` 
    : `linear-gradient(to bottom right, ${slateColors[50]}, white)`};
  border: 1px solid ${props => props.variant === 'features' 
    ? purpleColors[200] 
    : slateColors[300]};
  
  @media (min-width: 768px) {
    padding: 2.5rem;
  }
`;

const ColumnTitle = styled.h3<{ variant?: 'features' | 'usecases' }>`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${slateColors[900]};
  margin-bottom: 0.5rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid ${props => props.variant === 'features' 
    ? purpleColors[300] 
    : violetColors[500]};
  
  @media (min-width: 768px) {
    font-size: 1.75rem;
    margin-bottom: 1rem;
  }
`;

const FeatureCard = styled.div<{ variant?: 'features' | 'usecases' }>`
  padding: 1.5rem;
  border-radius: 1rem;
  background: white;
  border: 1px solid ${props => props.variant === 'features' 
    ? purpleColors[200] 
    : slateColors[300]};
  transition: all 0.3s;
  
  &:hover {
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
    border-color: ${props => props.variant === 'features' 
      ? purpleColors[400] 
      : violetColors[500]};
    transform: translateY(-2px);
    background: ${props => props.variant === 'features' 
      ? `${purpleColors[50]}` 
      : `${slateColors[100]}`};
  }
`;

const FeatureCardContent = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
`;

const FeatureIcon = styled.div<{ variant?: 'features' | 'usecases' }>`
  flex-shrink: 0;
  width: 3rem;
  height: 3rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.variant === 'features'
    ? `linear-gradient(to bottom right, ${purpleColors[500]}, ${purpleColors[600]})`
    : `linear-gradient(to bottom right, ${violetColors[500]}, ${violetColors[600]})`};
  border-radius: 0.75rem;
  transition: transform 0.2s;
  
  ${FeatureCard}:hover & {
    transform: scale(1.1);
  }
  
  svg {
    color: white;
    width: 1.5rem;
    height: 1.5rem;
    stroke-width: 2;
  }
`;

const FeatureText = styled.div`
  flex: 1;
`;

const FeatureTitle = styled.h4`
  font-size: 1.125rem;
  font-weight: 600;
  color: ${slateColors[900]};
  margin: 0 0 0.5rem 0;
  line-height: 1.4;
  
  @media (min-width: 768px) {
    font-size: 1.25rem;
  }
`;

const FeatureDescription = styled.p`
  font-size: 0.9375rem;
  color: ${slateColors[600]};
  line-height: 1.6;
  margin: 0;
  
  @media (min-width: 768px) {
    font-size: 1rem;
  }
`;

const FormSection = styled(Section)`
  background: linear-gradient(to bottom right, ${purpleColors[600]}, ${violetColors[600]});
  color: white;
`;

const FormContainer = styled(Container)`
  max-width: 42rem;
`;

const FormTitle = styled.h2`
  font-size: 2rem;
  font-weight: 700;
  color: white;
  margin-bottom: 1rem;
  text-align: center;
  
  @media (min-width: 768px) {
    font-size: 2.5rem;
    margin-bottom: 1.5rem;
  }
`;

const FormDescription = styled.p`
  font-size: 1.125rem;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 2rem;
  text-align: center;
  line-height: 1.6;
  
  @media (min-width: 768px) {
    font-size: 1.25rem;
    margin-bottom: 2.5rem;
  }
`;

const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;


const SubmitButton = styled.button`
  padding: 1rem 2rem;
  background: white;
  color: ${purpleColors[600]};
  border-radius: 0.75rem;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.2s;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  
  &:hover {
    background: ${slateColors[50]};
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
    transform: translateY(-2px);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  svg {
    width: 1.125rem;
    height: 1.125rem;
  }
`;

const SuccessMessage = styled.div`
  padding: 1.5rem;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  border-radius: 0.75rem;
  border: 2px solid rgba(255, 255, 255, 0.2);
  text-align: center;
`;

const SuccessText = styled.p`
  font-size: 1.125rem;
  font-weight: 600;
  color: white;
  margin: 0;
`;

const ErrorMessage = styled.div`
  padding: 1.5rem;
  background: rgba(239, 68, 68, 0.2);
  backdrop-filter: blur(10px);
  border-radius: 0.75rem;
  border: 2px solid rgba(239, 68, 68, 0.4);
  text-align: center;
`;

const ErrorTextMessage = styled.p`
  font-size: 1rem;
  font-weight: 500;
  color: #fecaca;
  margin: 0;
`;

interface FeatureProps {
    icon: React.ComponentType<{ size?: number; className?: string }>;
    title: string;
    body: string;
    variant?: 'features' | 'usecases';
}

const Feature = ({ icon: Icon, title, body, variant = 'features' }: FeatureProps) => {
    return (
        <FeatureCard variant={variant}>
            <FeatureCardContent>
                <FeatureIcon variant={variant}>
                    <Icon size={24} />
                </FeatureIcon>
                <FeatureText>
                    <FeatureTitle>{title}</FeatureTitle>
                    <FeatureDescription>{body}</FeatureDescription>
                </FeatureText>
            </FeatureCardContent>
        </FeatureCard>
    );
};

// List of countries shown on the data page (keys match dataPage.countries in locale files)
const dataPageCountries = [
  { key: 'belgium', flag: '🇧🇪' },
  { key: 'netherlands', flag: '🇳🇱' },
  { key: 'germany', flag: '🇩🇪' },
  { key: 'austria', flag: '🇦🇹' },
  { key: 'switzerland', flag: '🇨🇭' },
  { key: 'france', flag: '🇫🇷' },
  { key: 'italy', flag: '🇮🇹' },
  { key: 'denmark', flag: '🇩🇰' },
  { key: 'hungary', flag: '🇭🇺' },
  { key: 'poland', flag: '🇵🇱' },
];

// List of operator logos
const operatorLogos = [
  { name: 'Deutsche Bahn', path: '/images/Operator logos/Deutsche_Bahn_AG-Logo.imageset/Deutsche_Bahn_AG-Logo.svg' },
  { name: 'DSB', path: '/images/Operator logos/DSB_company_logo.imageset/DSB_company_logo.svg' },
  { name: 'European Sleeper', path: '/images/Operator logos/European_Sleeper_logo.imageset/European_Sleeper_logo.svg' },
  { name: 'Eurostar', path: '/images/Operator logos/eurostar.imageset/eurostar.svg' },
  { name: 'FlixTrain', path: '/images/Operator logos/FlixTrain_Logo_2020.imageset/FlixTrain_Logo_2020.svg' },
  { name: 'MAV', path: '/images/Operator logos/MAV-logo.imageset/MAV-logo.svg' },
  { name: 'NS', path: '/images/Operator logos/Nederlandse_Spoorwegen_logo.imageset/Nederlandse_Spoorwegen_logo.svg' },
  { name: 'ÖBB', path: '/images/Operator logos/Logo_OEBB.imageset/Logo_OEBB.svg' },
  { name: 'PKP', path: '/images/Operator logos/PKP.imageset/PKP.svg' },
  { name: 'SBB CFF FFS', path: '/images/Operator logos/SBB_CFF_FFS_logo.imageset/SBB_CFF_FFS_logo 2.svg' },
  { name: 'SNCB/NMBS', path: '/images/Operator logos/SNCB_logo.imageset/SNCB_logo.svg' },
  { name: 'SNCF', path: '/images/Operator logos/Sncf-logo.imageset/Sncf-logo.svg' },
  { name: 'Ouigo', path: '/images/Operator logos/TGV_Ouigo_2013_logo.imageset/TGV_Ouigo_2013_logo.svg' },
  { name: 'Trenitalia', path: '/images/Operator logos/Trenitalia_logo_RDT.imageset/Trenitalia_logo_RDT.svg' },
  { name: 'Westbahn', path: '/images/Operator logos/Westbahn_Logo.imageset/Westbahn_Logo.svg' },
];

const APIPage = () => {
    const { t } = useTranslation();
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [useCase, setUseCase] = useState("");
    const [isNameError, setIsNameError] = useState(false);
    const [isEmailError, setIsEmailError] = useState(false);
    const [emailErrorMessage, setEmailErrorMessage] = useState("");
    const [isUseCaseError, setIsUseCaseError] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    const getAccessRef = useRef<HTMLDivElement>(null);

    const validateEmail = (email: string) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };
  
    const handleSubmit = async () => {
      let hasError = false;
  
      if (!name) {
        setIsNameError(true);
        hasError = true;
      } else {
        setIsNameError(false);
      }
  
      if (!email) {
        setEmailErrorMessage(t('common.thisFieldRequired'));
        setIsEmailError(true);
        hasError = true;
      } else if (!validateEmail(email)) {
        setEmailErrorMessage(t('common.validEmailRequired'));
        setIsEmailError(true);
        hasError = true;
      } else {
        setIsEmailError(false);
      }
  
      if (!useCase) {
        setIsUseCaseError(true);
        hasError = true;
      } else {
        setIsUseCaseError(false);
      }
  
      if (!hasError) {
        const formData = {
            name,
            email,
            useCase,
        };
  
      try {
        const response = await postApi('send-api-email', formData);
  
        if (response.ok) {
          const data = await response.json();
          console.log(data.message);
          setIsSuccess(true);
          setName('');
          setEmail('');
          setUseCase('');
          setErrorMessage("");
        } else {
          const errorData = await response.json();
          console.error('Error:', errorData.message);
          setErrorMessage(errorData.message);
        }
      } catch (error) {
        console.error('Network error:', error);
        setErrorMessage(t('common.networkError'));
      }
    }
  };

    const handleScrollToForm = () => {
        getAccessRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const schemaData = {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "chuuchuu Train Delay Data API",
      "applicationCategory": "BusinessApplication",
      "offers": {
        "@type": "Offer",
        "price": "Contact for pricing"
      },
      "description": "Comprehensive European train delay analytics API with real-time and historical data"
    };

    return (
      <>
        <Helmet prioritizeSeoTags>
          <title>{DATA_PAGE_SEO.title}</title>
          <meta name="description" content={DATA_PAGE_SEO.description} />
          <meta name="keywords" content="train delay data, rail analytics API, European train data, train punctuality metrics, delay statistics, railway performance data" />
          <meta name="prerender-status-code" content="200" />
          <meta name="prerender-header" content="Cache-Control: max-age=300, must-revalidate" />
          <meta name="fragment" content="!" />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={DATA_PAGE_SEO.url} />
          <meta property="og:title" content={DATA_PAGE_SEO.title} />
          <meta property="og:description" content={DATA_PAGE_SEO.description} />
          <meta property="og:image" content={DATA_PAGE_SEO.image} />
          <meta property="twitter:card" content="summary_large_image" />
          <meta property="twitter:url" content={DATA_PAGE_SEO.url} />
          <meta property="twitter:title" content={DATA_PAGE_SEO.title} />
          <meta property="twitter:description" content={DATA_PAGE_SEO.description} />
          <meta property="twitter:image" content={DATA_PAGE_SEO.image} />
          <meta name="robots" content="index, follow" />
          <link rel="canonical" href="https://chuuchuu.com/data" />
          <script type="application/ld+json">
            {JSON.stringify(schemaData)}
          </script>
        </Helmet>

        <HeroSection>
          <HeroContent>
            <HeroTitle>
              {t('dataPage.title')} <TitleHighlight>{t('dataPage.titleHighlight')}</TitleHighlight>
            </HeroTitle>
            <HeroDescription>
              {t('dataPage.description')}
            </HeroDescription>
            <CTAButton onClick={handleScrollToForm}>
              {t('dataPage.getAccess')}
              <ArrowRight />
            </CTAButton>
          </HeroContent>
        </HeroSection>

        <ContentSection>
          <ContentContainer>
            <SectionTitle>
              {t('dataPage.sectionTitle')} <TitleHighlight>{t('dataPage.sectionTitleHighlight')}</TitleHighlight> {t('dataPage.sectionTitleEnd')}
            </SectionTitle>
            <SectionDescription>
              {t('dataPage.sectionDescription')}
            </SectionDescription>
            <CountriesSection>
              <CountriesTitle>{t('dataPage.availableCountries')}</CountriesTitle>
              <CountriesList>
                {dataPageCountries.map(({ key, flag }) => (
                  <CountryBadge key={key}>
                    <Flag role="img" aria-label={t('dataPage.countries.' + key) + ' flag'}>{flag}</Flag>
                    {t('dataPage.countries.' + key)}
                  </CountryBadge>
                ))}
              </CountriesList>
            </CountriesSection>
            <OperatorsSection>
              <OperatorsTitle>{t('dataPage.includedOperators')}</OperatorsTitle>
              <OperatorsGrid>
                {operatorLogos.map((operator) => (
                  <React.Fragment key={operator.name}>
                    <OperatorLogo 
                      data-tooltip-id={`operator-${operator.name}`}
                      data-tooltip-content={operator.name}
                    >
                      <img
                        src={operator.path}
                        alt={operator.name}
                        loading="lazy"
                      />
                    </OperatorLogo>
                    <StyledTooltip id={`operator-${operator.name}`} place="top" className="tooltip-styled" />
                  </React.Fragment>
                ))}
              </OperatorsGrid>
            </OperatorsSection>
          </ContentContainer>
        </ContentSection>

        <FeaturesSection>
          <FeaturesContainer>
            <FeaturesHeader>
              <FeaturesTitle>{t('dataPage.builtForUseCase')}</FeaturesTitle>
              <FeaturesDescription>
                {t('dataPage.featuresDescription')}
              </FeaturesDescription>
            </FeaturesHeader>
            <FeaturesGrid>
              <FeatureColumn variant="features">
                <ColumnTitle variant="features">{t('dataPage.features')}</ColumnTitle>
                <Feature icon={Clock} title={t('dataPage.delayHistory')} body={t('dataPage.delayHistoryBody')} variant="features" />
                <Feature icon={BarChart3} title={t('dataPage.statistics')} body={t('dataPage.statisticsBody')} variant="features" />
                <Feature icon={ArrowUpDown} title={t('dataPage.transfers')} body={t('dataPage.transfersBody')} variant="features" />
                <Feature icon={Plug} title={t('dataPage.easyToIntegrate')} body={t('dataPage.easyToIntegrateBody')} variant="features" />
                <Feature icon={Globe} title={t('dataPage.wideCoverage')} body={t('dataPage.wideCoverageBody')} variant="features" />
                <Feature icon={RefreshCw} title={t('dataPage.upToDate')} body={t('dataPage.upToDateBody')} variant="features" />
              </FeatureColumn>
              <FeatureColumn variant="usecases">
                <ColumnTitle variant="usecases">{t('dataPage.useCases')}</ColumnTitle>
                <Feature icon={Search} title={t('dataPage.research')} body={t('dataPage.researchBody')} variant="usecases" />
                <Feature icon={Brain} title={t('dataPage.delayPrediction')} body={t('dataPage.delayPredictionBody')} variant="usecases" />
                <Feature icon={Building2} title={t('dataPage.policy')} body={t('dataPage.policyBody')} variant="usecases" />
                <Feature icon={Activity} title={t('dataPage.monitoring')} body={t('dataPage.monitoringBody')} variant="usecases" />
                <Feature icon={Bus} title={t('dataPage.timetablePlanning')} body={t('dataPage.timetablePlanningBody')} variant="usecases" />
                <Feature icon={FileText} title={t('dataPage.reporting')} body={t('dataPage.reportingBody')} variant="usecases" />
              </FeatureColumn>
            </FeaturesGrid>
          </FeaturesContainer>
        </FeaturesSection>

        <FormSection ref={getAccessRef}>
          <FormContainer>
            <FormTitle>{t('dataPage.formTitle')}</FormTitle>
            <FormDescription>
              {t('dataPage.formDescription')}
            </FormDescription>
            {!isSuccess ? (
              <FormWrapper>
                <TextField
                  instruction={t('common.name')}
                  dynamicPlaceholder={t('dataPage.namePlaceholder')}
                  isDisabled={false}
                  isError={isNameError}
                  errorMessage={t('common.thisFieldRequired')}
                  value={name}
                  isBackgroundDark={true}
                  labelColor="rgba(255, 255, 255, 0.95)"
                  borderColor="rgba(255, 255, 255, 0.2)"
                  backgroundColor="rgba(255, 255, 255, 0.1)"
                  textColor="white"
                  placeholderColor="rgba(255, 255, 255, 0.6)"
                  focusBorderColor="rgba(255, 255, 255, 0.5)"
                  focusBackgroundColor="rgba(255, 255, 255, 0.15)"
                  backdropFilter="blur(10px)"
                  onChange={(e) => {
                    setName(e.target.value);
                    if (isNameError) setIsNameError(false);
                  }}
                />
                <TextField
                  instruction={t('common.email')}
                  dynamicPlaceholder={t('dataPage.emailPlaceholder')}
                  isDisabled={false}
                  isError={isEmailError}
                  errorMessage={emailErrorMessage}
                  value={email}
                  isBackgroundDark={true}
                  labelColor="rgba(255, 255, 255, 0.95)"
                  borderColor="rgba(255, 255, 255, 0.2)"
                  backgroundColor="rgba(255, 255, 255, 0.1)"
                  textColor="white"
                  placeholderColor="rgba(255, 255, 255, 0.6)"
                  focusBorderColor="rgba(255, 255, 255, 0.5)"
                  focusBackgroundColor="rgba(255, 255, 255, 0.15)"
                  backdropFilter="blur(10px)"
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (isEmailError) setIsEmailError(false);
                  }}
                />
                <MultiLineTextField
                  instruction={t('dataPage.describeUseCase')}
                  dynamicPlaceholder={t('dataPage.useCasePlaceholder')}
                  isDisabled={false}
                  isError={isUseCaseError}
                  errorMessage={t('common.thisFieldRequired')}
                  value={useCase}
                  isBackgroundDark={true}
                  labelColor="rgba(255, 255, 255, 0.95)"
                  borderColor="rgba(255, 255, 255, 0.2)"
                  backgroundColor="rgba(255, 255, 255, 0.1)"
                  textColor="white"
                  placeholderColor="rgba(255, 255, 255, 0.6)"
                  focusBorderColor="rgba(255, 255, 255, 0.5)"
                  focusBackgroundColor="rgba(255, 255, 255, 0.15)"
                  backdropFilter="blur(10px)"
                  onChange={(e) => {
                    setUseCase(e.target.value);
                    if (isUseCaseError) setIsUseCaseError(false);
                  }}
                />
                <SubmitButton onClick={handleSubmit}>
                  {t('dataPage.requestDataAccess')}
                  <ArrowRight />
                </SubmitButton>
                {errorMessage && (
                  <ErrorMessage>
                    <ErrorTextMessage>{errorMessage}</ErrorTextMessage>
                  </ErrorMessage>
                )}
              </FormWrapper>
            ) : (
              <SuccessMessage>
                <SuccessText>{t('dataPage.successMessage')}</SuccessText>
              </SuccessMessage>
            )}
          </FormContainer>
        </FormSection>
      </>
    );
};
  
export default APIPage;
